import { FieldValues, FormState, UseFormRegister, UseFormSetValue } from 'react-hook-form';

import { FieldLayout, Fieldset, FormField, Input, Label } from '@anwb/poncho/components/forms';
import Panel from '@anwb/poncho/components/panel';
import { useTransformVisualValue } from '@anwb/poncho/utilities/form-helpers';
import { formatEmail, formatTelephone } from '@anwb/poncho/utilities/formatters';
import emailRegexValidation from '@anwb/poncho/validations/validations-email';
import { regex as telephoneRegexValidation } from '@anwb/poncho/validations/validations-telephone';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import { PLCustomerData } from 'types';

import { handleFormGaEvent } from '../utils';

import { StyledTypography } from './styles/fieldset-caption.styled';

type ContactInformationProps = {
	register: UseFormRegister<FieldValues>;
	formState: FormState<PLCustomerData>;
	setValue: UseFormSetValue<FieldValues>;
	trigger: (name?: string | string[]) => Promise<boolean>;
};

export const ContactInformation = ({
	register,
	formState,
	setValue,
	trigger,
}: ContactInformationProps) => {
	const [, transformedEmail, handleEmailChange] = useTransformVisualValue('', [formatEmail]);
	const [, transformedPhoneNumber, handlePhoneNumberChange] = useTransformVisualValue('', [
		formatTelephone,
	]);
	const gaFunnelInfo = useGaFunnelInfo();

	return (
		<Panel hasOuterSpacing={false}>
			<Panel.Content>
				<Fieldset
					legend={
						<StyledTypography variant="component-title" tagName="h2">
							Contactgegevens
						</StyledTypography>
					}
				>
					<FieldLayout>
						<FormField
							size="m"
							label={<Label text="Telefoonnummer" reference="phoneNumber" required />}
						>
							<Input
								reference="phoneNumber"
								placeholder="Bijv.: 06 12345678"
								{...register('phoneNumber', {
									required: 'Je hebt je telefoonnummer nog niet ingevuld.',
									pattern: {
										value: new RegExp(telephoneRegexValidation),
										message: 'Je hebt je telefoonnummer onjuist ingevuld.',
									},
								})}
								onChange={(event) => {
									handlePhoneNumberChange(event);
									setValue('phoneNumber', event.target.value);
								}}
								onBlur={async (event) => {
									if (transformedPhoneNumber) {
										setValue('phoneNumber', transformedPhoneNumber, { shouldDirty: true });
										await trigger('phoneNumber');
									}
									handleFormGaEvent('phoneNumber', formState, event, gaFunnelInfo);
								}}
							/>

							{formState.errors.phoneNumber && (
								<FormField.Error aria-live="assertive">
									{formState.errors.phoneNumber.message}
								</FormField.Error>
							)}
						</FormField>
					</FieldLayout>
					<FieldLayout>
						<FormField size="m" label={<Label text="E-mailadres" reference="email" required />}>
							<Input
								reference="email"
								placeholder="Bijv.: voorbeeld@domein.nl"
								{...register('email', {
									required: 'Je hebt je e-mailadres nog niet ingevuld.',
									pattern: {
										value: new RegExp(emailRegexValidation),
										message: 'Je hebt je e-mailadres onjuist ingevuld.',
									},
								})}
								onChange={(event) => {
									handleEmailChange(event);
									setValue('email', event.target.value);
								}}
								onBlur={async (event) => {
									if (transformedEmail) {
										setValue('email', transformedEmail, { shouldDirty: true });
										await trigger('email');
									}
									handleFormGaEvent('email', formState, event, gaFunnelInfo);
								}}
							/>

							{formState.errors.email && (
								<FormField.Error aria-live="assertive">
									{formState.errors.email.message}
								</FormField.Error>
							)}
						</FormField>
					</FieldLayout>
				</Fieldset>
			</Panel.Content>
		</Panel>
	);
};
